import { useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, ShieldCheck, Check } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tilt3D } from "@/components/Tilt3D";
import { useCart } from "@/context/CartContext";

export const ProductCard = ({ product, allProducts, onChangeModel, index }) => {
  const { addItem, setReserveOpen } = useCart();
  const [colorIdx, setColorIdx] = useState(0);
  const [shapeIdx, setShapeIdx] = useState(0);
  const [lensIdx, setLensIdx] = useState(0);
  const [qty, setQty] = useState(1);

  const color = product.colors[colorIdx];
  const shapes = product.shapes || [];
  const lenses = product.lenses || [];
  const shape = shapes[shapeIdx]?.name;
  const lens = lenses[lensIdx]?.name;
  const lensAddon = lenses[lensIdx]?.addon || 0;
  const unitPrice = product.price + lensAddon;
  const total = unitPrice * qty;

  const box = (active) =>
    `flex items-center justify-between gap-2 w-full text-left rounded-xl border px-4 py-3 text-sm font-medium transition-all duration-200 ${
      active ? "border-zinc-900 ring-1 ring-zinc-900 text-zinc-900" : "border-zinc-200 text-zinc-600 hover:border-zinc-400"
    }`;

  const buildItem = () => ({
    product_id: product.id,
    product_name: product.name,
    color: color.name,
    shape,
    lens,
    quantity: qty,
    unit_price: unitPrice,
    image: color.image,
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.55, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`product-card-${product.id}`}
      className="flex flex-col items-center text-center px-2 md:px-6"
    >
      {/* Model dropdown */}
      <Select value={product.id} onValueChange={onChangeModel}>
        <SelectTrigger
          data-testid={`model-select-${index}`}
          className="w-full max-w-xs h-12 rounded-xl border-zinc-200 text-base font-medium justify-between"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {allProducts.map((p) => (
            <SelectItem key={p.id} value={p.id} data-testid={`model-option-${p.id}`}>
              {p.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Image */}
      <Tilt3D max={12} scale={1.03} glare testid={`tilt-${product.id}`} className="w-full mt-8">
        <div className="w-full rounded-3xl overflow-hidden bg-[#F5F5F7] aspect-[4/3] shadow-[0_20px_50px_rgb(0,0,0,0.06)]">
          <motion.img
            key={color.name}
            initial={{ opacity: 0.4, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            src={color.image}
            alt={`${product.name} in ${color.name}`}
            className="w-full h-full object-cover"
            data-testid={`product-image-${product.id}`}
          />
        </div>
      </Tilt3D>

      {/* Color swatches */}
      <div className="mt-6 flex items-center justify-center gap-3">
        {product.colors.map((c, ci) => (
          <button
            key={c.name}
            data-testid={`color-${product.id}-${c.name}`}
            onClick={() => setColorIdx(ci)}
            title={c.name}
            className={`w-7 h-7 rounded-full transition-all duration-200 ${
              colorIdx === ci ? "ring-2 ring-offset-2 ring-zinc-900" : "ring-1 ring-black/10 hover:ring-zinc-300"
            }`}
            style={{ backgroundColor: c.hex }}
          />
        ))}
      </div>
      <p className="mt-3 text-sm text-zinc-500">{color.name}</p>

      {/* Frame shape */}
      {shapes.length > 0 && (
        <div className="mt-6 w-full max-w-xs mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2">Frame shape</div>
          <div className="grid grid-cols-1 gap-2">
            {shapes.map((s, si) => (
              <button
                key={s.name}
                data-testid={`shape-${product.id}-${s.name}`}
                onClick={() => setShapeIdx(si)}
                className={box(shapeIdx === si)}
              >
                <span>{s.name}</span>
                {shapeIdx === si && <Check className="w-4 h-4 text-zinc-900" />}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Lens */}
      {lenses.length > 0 && (
        <div className="mt-4 w-full max-w-xs mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2">Lens</div>
          <div className="grid grid-cols-1 gap-2">
            {lenses.map((l, li) => (
              <button
                key={l.name}
                data-testid={`lens-${product.id}-${l.name}`}
                onClick={() => setLensIdx(li)}
                className={box(lensIdx === li)}
              >
                <span>{l.name}</span>
                <span className="flex items-center gap-2">
                  {l.addon > 0 && (
                    <span
                      data-testid={`lens-addon-${product.id}-${l.name}`}
                      className={`text-xs font-semibold ${lensIdx === li ? "text-zinc-900" : "text-zinc-400"}`}
                    >
                      +${l.addon}
                    </span>
                  )}
                  {lensIdx === li && <Check className="w-4 h-4 text-zinc-900" />}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Name + tagline */}
      <h3 className="mt-6 font-display text-2xl font-extrabold tracking-tight text-zinc-900">
        {product.name}
      </h3>
      <p className="mt-1 text-sm text-zinc-400">{product.tagline}</p>

      {/* Price */}
      <p className="mt-4 text-base text-zinc-900">
        VIP launch price ·{" "}
        <span data-testid={`product-price-${product.id}`} className="font-semibold">
          ${total.toFixed(0)}
        </span>
      </p>

      {/* Quantity */}
      <div className="mt-4 flex items-center border border-zinc-200 rounded-full">
        <button
          data-testid={`qty-dec-${product.id}`}
          onClick={() => setQty((q) => Math.max(1, q - 1))}
          className="p-2 text-zinc-600 hover:text-zinc-900 transition-colors"
        >
          <Minus className="w-4 h-4" />
        </button>
        <span data-testid={`qty-value-${product.id}`} className="px-3 text-sm w-8 text-center">
          {qty}
        </span>
        <button
          data-testid={`qty-inc-${product.id}`}
          onClick={() => setQty((q) => q + 1)}
          className="p-2 text-zinc-600 hover:text-zinc-900 transition-colors"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Actions */}
      <div className="mt-5 flex items-center gap-4">
        <button
          data-testid={`buy-now-${product.id}`}
          onClick={() => {
            addItem(buildItem(), false);
            setReserveOpen(true);
          }}
          className="bg-zinc-900 text-white text-sm font-semibold px-8 py-2.5 rounded-full hover:bg-zinc-800 active:scale-[0.98] transition-all duration-200"
        >
          Join VIP Access
        </button>
        <button
          data-testid={`add-to-cart-${product.id}`}
          onClick={() => {
            addItem(buildItem(), false);
            setReserveOpen(true);
          }}
          className="text-sm font-medium text-zinc-700 hover:text-zinc-900 transition-colors underline-offset-4 hover:underline"
        >
          Add another pair
        </button>
      </div>

      <div className="mt-3 flex items-center gap-1.5 text-xs text-zinc-500">
        <ShieldCheck className="w-4 h-4 text-emerald-600" />
        Free to join · No payment · No credit card required
      </div>

      <p className="mt-3 text-sm text-zinc-500 leading-relaxed max-w-sm">{product.description}</p>

      {/* Specs */}
      <div className="w-full mt-10 pt-8 border-t border-zinc-200">
        <dl className="flex flex-col gap-6">
          {product.specs.map((s) => (
            <div key={s.label} className="flex flex-col items-center gap-1">
              <dt className="text-xs uppercase tracking-[0.15em] text-zinc-400">{s.label}</dt>
              <dd className="text-sm md:text-base text-zinc-900 font-medium">{s.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </motion.div>
  );
};
